import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useTenantTranslation } from "hooks/formatters";
import { useState } from "react";
import { useGetRunConditions } from "./RunConditions";
import { Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useIsAdmin } from "hooks/settings";
import { useMiscParams } from "hooks/search";

interface Props {
  onSubmit: () => void;
  loading: boolean;
}

export const RunSearchButtonGroup = ({ onSubmit, loading }: Props) => {
  const { t } = useTenantTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const isAdmin = useIsAdmin();
  const [, setMiscParams] = useMiscParams();
  const runConditions = useGetRunConditions();

  const options = [t("searchPlans"), t("longOptimisation")];

  const handleMenuItemClick = (index: number) => {
    setSelectedIndex(index);
    setAnchorEl(null);
    if (index === 0) {
      // Turn off deep search
      setMiscParams((current) => ({
        ...current,
        optimisation_time_limit: null,
      }));
    }
    if (index === 1) {
      // Turn on deep search
      setMiscParams((current) => ({
        ...current,
        optimisation_time_limit: 120,
      }));
    }
  };

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const conditionsLoading = runConditions === null;
  const areRunConditionsMet = runConditions?.areRunConditionsMet ?? false;

  return (
    <>
      <Tooltip
        title={
          conditionsLoading || areRunConditionsMet ? "" : t("searchPlansText")
        }
      >
        <ButtonGroup
          variant="contained"
          disabled={conditionsLoading || !areRunConditionsMet}
          disableElevation
        >
          <LoadingButton
            onClick={onSubmit}
            loading={loading || conditionsLoading}
          >
            {options[selectedIndex]}
          </LoadingButton>
          {isAdmin ? (
            <Button
              size="small"
              onClick={handleToggle}
              sx={{ p: 0 }}
              disabled={loading || conditionsLoading}
            >
              <ArrowDropDownIcon />
            </Button>
          ) : null}
        </ButtonGroup>
      </Tooltip>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ mt: 0.5 }}
        slotProps={{ list: { disablePadding: true } }}
      >
        {options.map((option, index) => (
          <MenuItem
            key={option}
            selected={index === selectedIndex}
            onClick={() => {
              handleMenuItemClick(index);
            }}
            sx={{ px: 2, py: 1 }}
          >
            <Typography variant="button">{option}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
